import type { ViewTypes } from 'src/helper/shared-types';
import type { ViewDetailsType, ViewListType } from 'src/redux/list-view/types';
import { WORK_ORDER_COLUMN_LABELS } from 'src/views/WorkOrder/workOrderColumns';

/**
 * The Work Orders "Default View" columns as Figma draws them (7453:980 table, 7506:10414 Manage
 * Columns): ID, Categories, Address, Status, Assignee(s), Priority, Created, in the Manage Columns
 * order. The server's default view also turns on Market, Provider and Tags and puts Priority
 * before Assignee(s), which made the list 11 columns wide and scroll sideways at 1456.
 */
const WORK_ORDER_DRAWN_DEFAULT_COLUMNS = new Set([
  'work_order_number',
  'category',
  'address',
  'status',
  'assignee',
  'priority',
  'created_date',
]);

const WORK_ORDER_COLUMN_ORDER = Object.keys(WORK_ORDER_COLUMN_LABELS);

const orderIndex = (value: string) => {
  const index = WORK_ORDER_COLUMN_ORDER.indexOf(value);
  return index === -1 ? WORK_ORDER_COLUMN_ORDER.length : index;
};

/**
 * Applies the drawn default columns to the Work Orders system "Default View": the primary view
 * that nobody can edit (the global row). The server also marks an organization's own editable
 * "Default View" copy as primary; that one, saved views and the other lists keep their saved
 * columns, so a Save of them sticks. Idempotent.
 */
export const withDrawnDefaultColumns = (
  viewType: ViewTypes,
  viewId: string,
  config: ViewDetailsType,
  viewList: ReadonlyArray<ViewListType> | undefined,
): ViewDetailsType => {
  if (viewType !== 'work-orders' || !config?.columns) return config;
  const isSystemDefault = viewList?.some(
    (item) => item?.view?.value === viewId && item.is_primary && item.is_editable === false,
  );
  if (!isSystemDefault) return config;
  const columns = config.columns
    .map((column) => ({
      ...column,
      selected: WORK_ORDER_DRAWN_DEFAULT_COLUMNS.has(column.value),
    }))
    .sort((a, b) => orderIndex(a.value) - orderIndex(b.value));
  return { ...config, columns };
};
